import { Box, IconButton } from "@mui/material"
import { DeleteOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { APPROVAL_STATUS_OPTIONS, PRIORITY_STATUS_OPTIONS } from "../../constants"

import { FormSelect } from "@/components/UiKit/FormControls"
import { FormMultiAutocomplete } from "@/components/UiKit/FormControls/FormMultiAutocomplete"
import type { AssociateProductFormValues } from "@/screens/Products/schemas/associateProductForm"
import type { Option } from "@/types/form-fields"

interface LocationRowFieldsProps {
  index: number
  locationOptions: Option<string>[]
  customerCodes: string[]
  onRemove?: (index: number) => void
  disableShipTo?: boolean
}

export const LocationRowFields: FC<LocationRowFieldsProps> = ({
  index,
  locationOptions,
  customerCodes,
  onRemove,
  disableShipTo,
}) => {
  const { control, setValue } = useFormContext<AssociateProductFormValues>()

  const existingCodeOptions = useMemo<Option<string>[]>(
    () => [...customerCodes].sort((a, b) => a.localeCompare(b)).map((code) => ({ value: code, label: code })),
    [customerCodes]
  )

  return (
    <Box className='flex flex-col gap-3' data-testid={`associate-location-row-${index}`}>
      <Box className='flex items-end gap-2'>
        <Box className={onRemove ? "w-1/2" : "flex-1"}>
          <FormSelect
            control={control}
            name={`locations.${index}.shipToLocationId`}
            label='Ship To'
            placeholder='Select...'
            options={locationOptions}
            disabled={disableShipTo}
            required
            data-testid={`associate-location-ship-to-${index}`}
          />
        </Box>
        {onRemove && (
          <IconButton
            onClick={() => onRemove(index)}
            aria-label='remove location'
            size='small'
            data-testid={`associate-location-remove-${index}`}
          >
            <DeleteOutlined />
          </IconButton>
        )}
      </Box>

      <FormMultiAutocomplete
        control={control}
        name={`locations.${index}.customerCode`}
        label='Customer Code(s)'
        options={existingCodeOptions}
        freeSolo
        filterSelectedOptions
        placeholder='Select or type...'
        onChange={(values) => setValue(`locations.${index}.customerCode`, [...new Set(values)])}
        data-testid={`associate-location-customer-code-${index}`}
      />

      <Box className='grid grid-cols-2 gap-3'>
        <FormSelect
          control={control}
          name={`locations.${index}.priorityStatus`}
          label='Priority'
          placeholder='Select...'
          options={PRIORITY_STATUS_OPTIONS as Option<string>[]}
          data-testid={`associate-location-priority-${index}`}
        />
        <FormSelect
          control={control}
          name={`locations.${index}.approvalStatus`}
          label='Status'
          placeholder='Select...'
          options={APPROVAL_STATUS_OPTIONS as Option<string>[]}
          data-testid={`associate-location-approval-${index}`}
        />
      </Box>
    </Box>
  )
}
